import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { HeatTraceClearInsulation, HeatTraceLoadInsulation } from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentInsulationTag, getInsulationBoxes, getInsulationLoading, getInsulations } from '@completion/selectors';

@Component({
  selector: 'app-insulation',
  templateUrl: './insulation.component.html',
  styleUrls: ['./insulation.component.scss'],
  standalone: false,
})
export class InsulationComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  tagNumber$ = this.route.paramMap.pipe(
    map((paramMap) => paramMap.get('tagNumber')),
    distinctUntilChanged(),
  );

  insulations$ = this.store.select(getInsulations);
  insulationBoxes$ = this.store.select(getInsulationBoxes);
  insulationLoading$ = this.store.select(getInsulationLoading);
  currentInsulationTag$ = this.store.select(getCurrentInsulationTag);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store<State>,
  ) {}

  ngOnInit(): void {
    this.tagNumber$
      .pipe(
        filter((tagNumber): tagNumber is string => tagNumber !== null),
        takeUntil(this.destroy$),
      )
      .subscribe((tagNumber) => {
        this.store.dispatch(new HeatTraceLoadInsulation(tagNumber));
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new HeatTraceClearInsulation());
    this.destroy$.next();
    this.destroy$.complete();
  }

  isRowClickable(row: Record<string, unknown>): boolean {
    return this.rowRoute(row) != null;
  }

  rowRoute(row: Record<string, unknown>): (string | number)[] | null {
    const cpId = this.getNumericId(row, 'cpId', 'cpID');
    const mcpId = this.getNumericId(row, 'mcpId', 'mcpID');
    if (cpId == null || mcpId == null) {
      return null;
    }
    return ['/cp', cpId, 'mcp', mcpId];
  }

  rowQueryParams(row: Record<string, unknown>): { tag: string } | null {
    const tagNumber = row['no'];
    if (typeof tagNumber !== 'string') {
      return null;
    }

    const tag = tagNumber.trim();
    return tag ? { tag } : null;
  }

  private getNumericId(row: Record<string, unknown>, ...keys: string[]): number | null {
    for (const key of keys) {
      const value = row[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
    return null;
  }
}
